import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { readLoadedDeploymentAssetVersion } from "../lib/deploymentUpdateRuntime.js";
import { createPerformanceCorrelationId } from "./sourceChangesRefreshPolicy.js";
import { createToastProgress, createToastProgressReporter, SOURCE_CHANGES_PROGRESS_STEPS } from "./toastProgress.js";

import { createMutationTransportDiagnostic } from "./mutationTransportDiagnostics.js";

const ENDPOINT = "/api/me/diagnostics/operation-captures";
const text = (value, limit) => typeof value === "string" ? value.slice(0, limit) : "";

const MUTATION_PHASES = new Set(["created", "request", "parse_response", "install_result", "publication_effects", "completion", "completed"]);
const ERROR_NAMES = new Set(["Error", "TypeError", "SyntaxError", "RangeError", "ReferenceError", "AbortError", "TimeoutError", "NetworkError", "ApiResponseError"]);
const RESPONSE_TYPES = new Set(["basic", "cors", "default", "error", "opaque", "opaqueredirect"]);
const evidenceValue = (read, fallback = null) => { try { return read(); } catch { return fallback; } };
const httpStatus = (value) => Number.isInteger(value) && value >= 0 && value <= 599 ? value : null;

// Keep classifications and bundle coordinates, never the error object, message, URL or response body.
function mutationErrorEvidence(error) {
  const name = evidenceValue(() => error?.name);
  const message = evidenceValue(() => error?.message);
  const code = evidenceValue(() => error?.code);
  const stack = evidenceValue(() => error?.stack);
  const stackLocations = typeof stack === "string"
    ? [...stack.slice(0, 16384).matchAll(/\/static\/react\/reassignment\.js(?:\?[^()\s]*)?:(\d{1,9}):(\d{1,9})(?=[)\s]|$)/g)]
      .slice(0, 3).map((match) => ({ line: Number(match[1]), column: Number(match[2]) }))
      .filter((location) => location.line > 0 && location.column > 0)
    : [];
  return {
    value_type: error === null ? "null" : typeof error,
    is_error_instance: evidenceValue(() => error instanceof Error, false),
    name: ERROR_NAMES.has(name) ? name : name == null ? "unknown" : "other",
    status: httpStatus(evidenceValue(() => error?.status)),
    code: typeof code === "string" && /^[a-z0-9_]{1,64}$/.test(code) ? code : null,
    message_kind: typeof message !== "string" || !message ? "empty"
      : message === "Failed to fetch" ? "failed_to_fetch"
        : message === "Impossible d'appliquer les changements Data Manager." ? "data_manager_fallback" : "other",
    stack_locations: stackLocations,
  };
}

function createMutationDiagnostic(options) {
  const transport = createMutationTransportDiagnostic(options);
  const version = evidenceValue(() => (options.readAssetVersion || readLoadedDeploymentAssetVersion)());
  const state = {
    phase: "created",
    loaded_asset_version: typeof version === "string" && /^[A-Za-z0-9_.-]{1,128}$/.test(version) ? version : null,
    response_received: false, response_status: null, response_ok: null, response_type: null,
    json_state: "not_read", json_error: null, error: null,
  };
  return {
    phase(value) {
      if (MUTATION_PHASES.has(value)) state.phase = value;
      if (value === "request") transport.request();
    },
    response(response) {
      transport.response(response);
      state.phase = "parse_response";
      state.response_received = true;
      state.response_status = httpStatus(evidenceValue(() => response?.status));
      const ok = evidenceValue(() => response?.ok);
      state.response_ok = typeof ok === "boolean" ? ok : null;
      const type = evidenceValue(() => response?.type);
      state.response_type = RESPONSE_TYPES.has(type) ? type : "other";
      state.json_state = "reading";
    },
    json(value, error, payload) {
      if (!["parsed", "invalid"].includes(value)) return;
      transport.json(value, error, payload);
      state.json_state = value;
      if (value === "invalid") state.json_error = mutationErrorEvidence(error);
    },
    fail(error) { transport.fail(error); if (!state.error) state.error = mutationErrorEvidence(error); },
    snapshot(responseAccepted) { return { ...state, response_accepted: responseAccepted, transport: transport.snapshot() }; },
  };
}

// Bounded evidence for long operations; never retain URLs, search parameters or raw errors.
function currentPage() {
  const path = globalThis.window?.location?.pathname;
  if (!path) return null;
  if (path === "/data-manager" || path.endsWith("/data-manager")) return "data_manager";
  if (/\/results(?:\/latest)?\/?$/.test(path)) return "dispatch";
  return "other";
}

function createOperationFeedbackDiagnostics({ readPage = currentPage } = {}) {
  const steps = new Map();
  const refreshPasses = new Map();
  const page = () => {
    try { const value = readPage(); return ["data_manager", "dispatch", "other"].includes(value) ? value : null; }
    catch { return null; }
  };
  const startedPage = page();
  let completedPage = startedPage, firstPageChangeStep = null;
  return {
    samplePage(step) {
      completedPage = page();
      if (startedPage && completedPage && completedPage !== startedPage && !firstPageChangeStep) {
        firstPageChangeStep = /^[a-z0-9_-]{1,64}$/.test(step || "") ? step : "completion";
      }
    },
    recordStep(key, details = {}) {
      if (key === "refresh" && details.pass_id && details.scope_key) {
        refreshPasses.set(details.pass_id, details);
        details = { ...details };
        for (const name of ["read_count", "superseded_count", "retry_count"]) {
          details[name] = [...refreshPasses.values()].reduce((sum, pass) => sum + (pass[name] || 0), 0);
        }
      }
      if (!/^[a-z0-9_-]{1,64}$/.test(key || "") || (!steps.has(key) && steps.size >= 16)) return;
      const next = { ...steps.get(key) };
      if (/^[a-z0-9_]{1,64}$/.test(details.result_code || "")) next.result_code = details.result_code;
      for (const name of ["read_count", "superseded_count", "retry_count"]) {
        if (Number.isInteger(details[name]) && details[name] >= 0) next[name] = Math.min(details[name], 1000);
      }
      steps.set(key, next);
    },
    stepDetails: (key) => steps.get(key) || {},
    navigation: () => startedPage && completedPage ? {
      started_page: startedPage, completed_page: completedPage,
      first_page_change_step: firstPageChangeStep,
    } : null,
  };
}

export function sourceChangeCaptureResourceContext(action, source = {}) {
  try {
    const details = source.details || {};
    const removedPm = (details.removed_end_of_day_loads || []).filter((item) => item.marker_key);
    const unmixedPm = removedPm.filter((item) => item.ghost_reason !== "source_mixed"
      && !(item.missing_order_ids?.length && item.date_changed_order_ids?.length));
    const orphanPm = unmixedPm.filter((item) => ["source_missing", "source_inactive"].includes(item.ghost_reason));
    const selections = {
      "import-new": details.new,
      "import-suspendable-errors": source.suspendableErrors?.details,
      "remove-modified": details.modified_same_date,
      "remove-date-changed": [...(details.date_changed_remove || []), ...unmixedPm.filter((item) => item.ghost_reason === "source_date_changed")],
      "repair-ghost-orders": [...(details.missing_dispatch_orders || []), ...orphanPm],
      "restore-executions": (details.inconsistent_execution_removals || []).filter((item) => item.recovery_eligible === true),
    };
    const selected = selections[action] || [];
    const ids = [...new Set(selected.flatMap((item) => item.order_ids || [item.order_id || item.orderId || item.delivery_id]).filter(Boolean))];
    return { resourceIds: ids, resourceCount: ids.length || null, resourceLabel: ids.length ? `${ids.length} commande(s)` : "Sélection de commandes" };
  } catch {
    return { resourceIds: [], resourceCount: null, resourceLabel: "Sélection de commandes" };
  }
}

export async function sendOperationFeedbackCapture(payload, { fetchImpl = fetchWithSupabaseAuth, timeoutMs = 5000 } = {}) {
  let timer;
  try {
    const controller = new AbortController();
    const expiry = new Promise((resolve) => {
      timer = setTimeout(() => { controller.abort(); resolve(false); }, timeoutMs);
    });
    return await Promise.race([
      Promise.resolve(fetchImpl(ENDPOINT, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload), signal: controller.signal,
      })).then((response) => response.ok === true),
      expiry,
    ]);
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// Changement du Gantt : son entrée Captures, écrite par le serveur, reçoit une fois la durée depuis le clic
// et le résultat de l'actualisation camion qui suit (ou son absence).
export function completeLinkedOperationCapture(linked, { status = "success", toastTitle = "", toastMessage = "", toastType = "success", steps = [] } = {},
  { now = () => performance.now(), send = sendOperationFeedbackCapture } = {}) {
  try {
    if (!linked?.operation_event_id || !linked.action || !Number.isFinite(linked.startedAt)) return;
    const payload = {
      operation_event_id: linked.operation_event_id, action: linked.action,
      toast_title: text(toastTitle, 256), toast_message: text(toastMessage, 16000), toast_type: toastType,
      toast_truncated: toastTitle.length > 256 || toastMessage.length > 16000,
      status, client_total_ms: Math.max(0, now() - linked.startedAt),
      dispatch_date: linked.dispatch_date || null, dispatch_periods: linked.dispatch_period ? [linked.dispatch_period] : [],
      resource_kind: "truck", resource_ids: [], resource_count: null, resource_ids_truncated: false,
      resource_label: text(linked.resource_label, 512), audit_ids: [],
      steps: [{ key: "change", label: "Enregistrement du changement", status: "completed",
        duration_ms: Number.isFinite(linked.changeMs) ? Math.max(0, linked.changeMs) : null }, ...steps].slice(0, 16),
    };
    void Promise.resolve().then(() => send(payload)).catch(() => {});
  } catch { /* Never interrupt the operation for its diagnostics. */ }
}

const noopCapture = {
  recordToast: (toast) => toast, recordResult() {}, markApplied() {}, setStatus() {}, fail() {}, finish() {},
  reportProgress() {}, getSnapshot: () => null, getStatus: () => null,
  recordStepDetails() {},
  recordMutationPhase() {}, recordMutationResponse() {}, recordMutationJson() {},
  getMutationCaptureContext: () => null,
  observeRefresh: (read) => read(),
};

// This object belongs to one handler lifecycle, never to browser storage or a global queue.
export function createOperationFeedbackCapture(options = {}) {
  try {
    const {
      action, operationEventId = createPerformanceCorrelationId(), dispatchDate = null,
      dispatchPeriods = [], resourceKind = "dispatch", resourceIds = [], resourceCount = null,
      resourceLabel = "", reporter, onProgress, now = () => performance.now(), send = sendOperationFeedbackCapture,
    } = options;
    const started = now();
    const allIds = [...new Set(resourceIds.filter((id) => id != null).map(String).filter(Boolean))];
    const context = {
      operation_event_id: operationEventId, action,
      dispatch_date: dispatchDate || null,
      dispatch_periods: [...new Set(dispatchPeriods.filter((period) => ["day", "evening"].includes(period)))],
      resource_kind: resourceKind,
      resource_ids: [...new Set(allIds.slice(0, 100).map((id) => id.slice(0, 128)))],
      resource_count: resourceCount ?? (allIds.length || null),
      resource_ids_truncated: allIds.length > 100 || (resourceCount != null && resourceCount > allIds.length) || allIds.some((id) => id.length > 128),
      resource_label: text(resourceLabel, 512),
      ...(options.origin ? { origin: options.origin } : {}),
      ...(options.mutationKind ? { mutation_kind: options.mutationKind } : {}),
      audit_ids: [],
      ...(options.orderEditSubmission ? { order_edit_submission: options.orderEditSubmission } : {}),
    };
    const report = reporter || createToastProgressReporter(createToastProgress(options.progressSteps || SOURCE_CHANGES_PROGRESS_STEPS), onProgress, { now });
    const evidence = createOperationFeedbackDiagnostics(options);
    // Target the import incident only. This is browser evidence, not proof of a server commit.
    const mutationDiagnostic = action === "source_changes_import_new" ? createMutationDiagnostic(options) : null;
    const samplePage = () => evidence.samplePage(report.getSnapshot()?.steps.find((step) => step.status === "running")?.key);
    let finalToast = null;
    let status = "success";
    let applied = false;
    let finished = false;
    const safe = (fn) => (...args) => { try { return fn(...args); } catch { return undefined; } };
    return {
      recordResult: safe((result) => {
        if (context.mutation_kind === "create") {
          const ids = resourceKind === "client" ? [result?.client_correspondence?.correspondence_id]
            : result?.created_order_ids || [result?.order?.order_id];
          const createdIds = [...new Set(ids.filter((id) => id != null).map(String).filter(Boolean))];
          if (createdIds.length) {
            context.resource_ids = createdIds.slice(0, 100).map((id) => id.slice(0, 128));
            context.resource_count = createdIds.length;
            context.resource_ids_truncated = createdIds.length > 100 || createdIds.some((id) => id.length > 128);
          }
        }
        const audits = result?.captureAudits || [result?.audit];
        context.audit_ids = [...new Set([...context.audit_ids, ...audits.map((audit) => audit?.audit_id).filter(Boolean)])].slice(0, 100);
        const label = result?.truck?.truck_name || audits.find((audit) => audit?.entity_kind !== "compartment")?.entity_label;
        if (label) context.resource_label = text(label, 512);
      }),
      reportProgress: safe((key, value, details) => {
        samplePage();
        if (details) evidence.recordStep(key, details);
        return report(key, value);
      }),
      recordStepDetails: safe((key, details) => { samplePage(); evidence.recordStep(key, details); }),
      getMutationCaptureContext: safe(() => mutationDiagnostic ? { schema_version: 1, action,
        operation_id: operationEventId, entities: { order_ids: context.resource_ids, order_count: context.resource_count },
        dispatch: { source_date: context.dispatch_date, target_period: context.dispatch_periods[0] },
        mutation_transport: mutationDiagnostic.snapshot(applied).transport,
        mutation_diagnostic: mutationDiagnostic.snapshot(applied) } : null),
      recordMutationPhase: safe((phase) => mutationDiagnostic?.phase(phase)),
      recordMutationResponse: safe((response) => mutationDiagnostic?.response(response)),
      recordMutationJson: safe((state, error, payload) => mutationDiagnostic?.json(state, error, payload)),
      getSnapshot: safe(() => report.getSnapshot()),
      getStatus: safe(() => status),
      observeRefresh: async (read, validate = (value) => Boolean(value)) => {
        safe(report)("refresh");
        try {
          const value = await read();
          if (!validate(value)) {
            status = applied ? "partial" : ["refused", "awaiting_confirmation"].includes(status) ? status : "failed";
            safe(report)("refresh", "failed");
          } else safe(report)("refresh", "completed");
          return value;
        } catch (error) {
          status = applied ? "partial" : ["refused", "awaiting_confirmation"].includes(status) ? status : "failed";
          safe(report)("refresh", "failed");
          throw error;
        }
      },
      markApplied: safe(() => { applied = true; }),
      setStatus: safe((value) => { status = status === "partial" && applied ? "partial" : value; }),
      fail: safe((error) => {
        evidenceValue(() => mutationDiagnostic?.fail(error));
        status = applied ? "partial" : ["refused", "awaiting_confirmation"].includes(status) ? status
          : Number(error?.status ?? error?.cause?.status) === 409 ? "refused" : "failed";
        const running = report.getSnapshot()?.steps.find((step) => step.status === "running");
        if (running) report(running.key, running.key !== "refresh" && status === "refused" ? "warning" : "failed");
      }),
      recordToast: (toast) => {
        try {
          if (["success", "warning", "error", "info"].includes(toast?.type)) {
            finalToast = { toast_title: text(toast.title, 256), toast_message: text(toast.message, 16000),
              toast_type: toast.type, toast_truncated: (toast.title?.length || 0) > 256 || (toast.message?.length || 0) > 16000 };
          }
        } catch { /* Feedback must never replace the original toast or error. */ }
        return toast;
      },
      finish: safe((timing = {}) => {
        if (finished) return;
        finished = true;
        samplePage();
        const clientTotalMs = Number.isFinite(timing.totalVisibleMs) ? Math.max(0, timing.totalVisibleMs) : Math.max(0, now() - started);
        const snapshot = report.finish({ status: status === "failed" ? "failed" : ["partial", "refused", "awaiting_confirmation"].includes(status) ? "warning" : "completed" });
        if (!finalToast) return;
        const steps = [...(snapshot?.steps || [])];
        // The global duration includes the decision time; processing steps exclude it.
        if (snapshot?.pausedDurationMs > 0) steps.splice(1, 0, {
          key: "confirmation", label: "Attente de confirmation", status: "completed",
          duration_ms: snapshot.pausedDurationMs,
        });
        const payload = { ...context, ...finalToast, status, client_total_ms: clientTotalMs,
          ...(mutationDiagnostic ? { mutation_diagnostic: mutationDiagnostic.snapshot(applied) } : {}),
          ...(evidence.navigation() ? { navigation: evidence.navigation() } : {}),
          steps: steps.filter((step) => ["completed", "warning", "failed", "skipped"].includes(step.status)).slice(0, 16)
            .map((step) => ({ key: text(step.key, 64), label: text(step.label, 160), status: step.status, duration_ms: step.duration_ms ?? null,
              ...evidence.stepDetails(step.key) })),
        };
        // Exclude delivery time from the measurement and absorb asynchronous rejection too.
        void Promise.resolve().then(() => send(payload)).catch(() => {});
        if (options.linkedCapture) completeLinkedOperationCapture(options.linkedCapture, { status, toastTitle: finalToast.toast_title,
          toastMessage: finalToast.toast_message, toastType: finalToast.toast_type, steps: payload.steps }, { now, send });
      }),
    };
  } catch {
    return noopCapture;
  }
}
